import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PartnerDetailPanel, Spinner, useIsMobile } from 'react-ui';
import { usePartnerScope } from '../contexts/PartnerScope';
import { useMapBoundingBox } from '../contexts/MapBoundingBox.tsx';
import { CloseButton } from '../components/CloseButton.tsx';
import { CloseMobileDialogFooterButton } from '../components/CloseMobileDialogFooterButton.tsx';

/**
 * Right-side slide-over hosting the public PARTNER profile in partner-scoped map
 * mode. Mirrors ProfileDetailPage (user) / EventDetailPage. Open on
 * `/partner/:code`. Distinct from the existing sponsor `PartnerDetailPage.tsx`.
 */
export function PartnerScopeDetailPage() {
    const { code, profile, loading, notFound, hasEvents, paneOpen, closeProfilePane } = usePartnerScope();
    const isMobile = useIsMobile();
    const mapBoundingBox = useMapBoundingBox();
    const navigate = useNavigate();

    if (!code || (!paneOpen && !notFound)) return null;

    // Without events there is no scoped list/header behind the pane — closing
    // it would leave a bare generic map parked on /partner/{code}, so go home.
    const close = () => (notFound || !hasEvents ? navigate('/') : closeProfilePane());

    const mobileStyles: React.HTMLAttributes<HTMLDivElement> = isMobile
        ? {
              style: {
                  zIndex: 1000,
                  position: 'fixed',
                  top: `${mapBoundingBox.top || 0}px`,
                  left: `${mapBoundingBox.left || 0}px`,
                  width: `${mapBoundingBox.width || 0}px`,
                  maxWidth: `${mapBoundingBox.width || 0}px`,
                  height: `${mapBoundingBox.height || 0}px`,
              },
          }
        : {};

    return (
        <div
            {...mobileStyles}
            className="partner-details slide-in-left absolute h-full top-0 left-[390px] w-[690px] flex flex-col bg-white backdrop-blur-md shadow-[2px_0px_6px_2px_rgba(0,0,0,0.20)]"
        >
            <div className="close-button-container absolute z-50 justify-center items-center p-0 border-0 border-gray-200 rounded-full top-4 right-6 backdrop-blur-sm bg-black/30">
                <CloseButton onClick={close} />
            </div>

            <div className="overflow-y-scroll flex-1 pointer-events-auto">
                {notFound ? (
                    <div className="p-10 flex flex-col gap-3 justify-center items-center h-full text-center">
                        <p className="text-lg font-semibold text-gray-900">Partner not found</p>
                        <p className="text-sm text-gray-500">
                            This partner doesn’t exist or is no longer available.
                        </p>
                        <button
                            type="button"
                            onClick={() => navigate('/')}
                            className="mt-2 px-4 py-2 text-sm rounded-full bg-balloon text-white hover:opacity-90 transition-opacity"
                        >
                            Back to all events
                        </button>
                    </div>
                ) : loading || !profile ? (
                    <div className="p-10 flex justify-center items-center h-full">
                        <Spinner />
                    </div>
                ) : (
                    <PartnerDetailPanel profile={profile} />
                )}
            </div>

            {isMobile && (
                <div className="flex flex-row items-center px-4 py-2 w-full border-t border-gray-200">
                    <CloseMobileDialogFooterButton onClick={close} />
                </div>
            )}
        </div>
    );
}

export default PartnerScopeDetailPage;
